// CanalesResumen — /admin/canales
// Vista comparada de los 3 canales activos: limpack.uy · MercadoLibre · B2B Empresas.
// Mismo catálogo, mismo stock, distintos clientes y precios.

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import AdminLayout from '../../components/AdminLayout'
import { supabase } from '../../lib/supabase'

export default function CanalesResumen() {
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState({
    web:  { ventas_mes: 0, pedidos: 0, ticket: 0, margen_pct: 0 },
    ml:   { ventas_mes: 0, pedidos: 0, ticket: 0, margen_pct: 0, items_publicados: 0, preguntas_pendientes: 0 },
    b2b:  { ventas_mes: 0, pedidos: 0, ticket: 0, margen_pct: 0 },
    stock_total: 0,
    alertas: [],
  })

  useEffect(() => {
    let cancelled = false
    async function cargar() {
      setLoading(true)
      const inicioMes = new Date(); inicioMes.setDate(1); inicioMes.setHours(0,0,0,0)
      const inicioISO = inicioMes.toISOString()

      // KPIs ML
      const [{ count: itemsML }, { count: pregsML }, { data: ventasML }] = await Promise.all([
        supabase.from('ml_publicaciones').select('id', { count: 'exact', head: true }).eq('status', 'active'),
        supabase.from('ml_preguntas').select('id', { count: 'exact', head: true }).eq('status', 'pending'),
        supabase.from('ml_ventas').select('monto_bruto, neto').gte('date_created', inicioISO),
      ])

      const ventasMlSum = (ventasML || []).reduce((acc, v) => acc + Number(v.monto_bruto || 0), 0)
      const netoMlSum  = (ventasML || []).reduce((acc, v) => acc + Number(v.neto || 0), 0)
      const margenMlPct = ventasMlSum > 0 ? Math.round((netoMlSum / ventasMlSum) * 100) : 0

      // KPI web (pedidos + suscripciones, simplificado) — solo reales
      const { data: pedidosWeb } = await supabase
        .from('pedidos')
        .select('total, estado, created_at')
        .eq('es_prueba', false)
        .gte('created_at', inicioISO)

      const ventasWebSum = (pedidosWeb || []).reduce((acc, p) => acc + Number(p.total || 0), 0)

      // KPI B2B (presupuestos aceptados del mes)
      const { data: presuB2b } = await supabase
        .from('presupuestos')
        .select('total')
        .gte('created_at', inicioISO)
        .eq('estado', 'aceptado')

      const ventasB2bSum = (presuB2b || []).reduce((acc, p) => acc + Number(p.total || 0), 0)

      // Stock disponible
      const { data: stockRows } = await supabase
        .from('productos')
        .select('stock_actual, stock_reservado')
        .is('deleted_at', null)
        .eq('activo', true)

      const stockTotal = (stockRows || []).reduce((acc, p) => acc + Math.max(0, Number(p.stock_actual || 0) - Number(p.stock_reservado || 0)), 0)

      const totalMes = ventasWebSum + ventasMlSum + ventasB2bSum

      const alertas = []
      if (pregsML && pregsML > 0) alertas.push({ tipo: 'amber', txt: `${pregsML} pregunta(s) ML sin responder. SLA pasivo ML < 24h.` })

      if (!cancelled) {
        setData({
          web:  { ventas_mes: ventasWebSum, pedidos: (pedidosWeb || []).length, ticket: pedidosWeb?.length ? ventasWebSum / pedidosWeb.length : 0, margen_pct: 36, share: totalMes > 0 ? Math.round(ventasWebSum / totalMes * 100) : 0 },
          ml:   { ventas_mes: ventasMlSum, pedidos: (ventasML || []).length, ticket: ventasML?.length ? ventasMlSum / ventasML.length : 0, margen_pct: margenMlPct, share: totalMes > 0 ? Math.round(ventasMlSum / totalMes * 100) : 0, items_publicados: itemsML || 0, preguntas_pendientes: pregsML || 0 },
          b2b:  { ventas_mes: ventasB2bSum, pedidos: (presuB2b || []).length, ticket: presuB2b?.length ? ventasB2bSum / presuB2b.length : 0, margen_pct: 28, share: totalMes > 0 ? Math.round(ventasB2bSum / totalMes * 100) : 0 },
          total_mes: totalMes,
          stock_total: stockTotal,
          alertas,
        })
        setLoading(false)
      }
    }
    cargar()
    return () => { cancelled = true }
  }, [])

  const fmt = (n) => '$ ' + Number(n || 0).toLocaleString('es-UY', { maximumFractionDigits: 0 })

  return (
    <AdminLayout title="Canales">
      <div className="cr-wrap">
        <header className="cr-head">
          <div>
            <div className="cr-eyebrow">CANALES · RESUMEN</div>
            <h1 className="cr-title">Cómo vendés en cada canal</h1>
            <div className="cr-sub">3 canales activos · mismo catálogo, mismo stock, distinto cliente y distinto precio.</div>
          </div>
        </header>

        <div className="cr-kpis">
          <div className="cr-kpi">
            <div className="cr-kpi-lbl">Ventas total mes</div>
            <div className="cr-kpi-num">{loading ? '…' : fmt(data.total_mes)}</div>
            <div className="cr-kpi-delta mute">junio 2026</div>
          </div>
          <div className="cr-kpi">
            <div className="cr-kpi-lbl">Pedidos mes</div>
            <div className="cr-kpi-num">{loading ? '…' : ((data.web?.pedidos || 0) + (data.ml?.pedidos || 0) + (data.b2b?.pedidos || 0))}</div>
            <div className="cr-kpi-delta mute">sumados todos los canales</div>
          </div>
          <div className="cr-kpi">
            <div className="cr-kpi-lbl">Ticket promedio</div>
            <div className="cr-kpi-num">{loading ? '…' : (() => {
              const totalPed = (data.web?.pedidos || 0) + (data.ml?.pedidos || 0) + (data.b2b?.pedidos || 0)
              return totalPed > 0 ? fmt(data.total_mes / totalPed) : '$ 0'
            })()}</div>
            <div className="cr-kpi-delta mute">global</div>
          </div>
          <div className="cr-kpi">
            <div className="cr-kpi-lbl">Stock disponible</div>
            <div className="cr-kpi-num">{loading ? '…' : data.stock_total}</div>
            <div className="cr-kpi-delta mute">unidades activas</div>
          </div>
        </div>

        <div className="cr-canales">
          <CanalCard
            color="#00A8B5"
            icon="🌐"
            nombre="limpack.uy"
            tipo="Suscripción mensual · kits + sueltos"
            data={data.web}
            href="/admin/clientes"
            loading={loading}
            fmt={fmt}
          />
          <CanalCard
            color="#FFE600"
            icon="🟡"
            nombre="MercadoLibre"
            tipo="@LIMPACKUY · productos sueltos premium"
            data={data.ml}
            href="/admin/canales/mercadolibre"
            loading={loading}
            fmt={fmt}
            extra={
              <div className="cr-card-extra">
                <div><span>Items publicados</span><strong>{data.ml.items_publicados}</strong></div>
                <div><span>Preguntas pendientes</span><strong style={{ color: data.ml.preguntas_pendientes > 0 ? '#BA7517' : '#1A3461' }}>{data.ml.preguntas_pendientes}</strong></div>
              </div>
            }
          />
          <CanalCard
            color="#1A3461"
            icon="🏢"
            nombre="B2B Empresas"
            tipo="Presupuestos aceptados · factura 60d"
            data={data.b2b}
            href="/admin/empresas"
            loading={loading}
            fmt={fmt}
          />
        </div>

        {data.alertas && data.alertas.length > 0 && (
          <div className="cr-card">
            <h3>Alertas activas</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {data.alertas.map((a, i) => (
                <div key={i} className={`cr-alert ${a.tipo}`}>{a.txt}</div>
              ))}
            </div>
          </div>
        )}

        <div className="cr-card">
          <h3>Distribución de ventas por canal (mes)</h3>
          <div className="cr-bar">
            <div style={{ background: '#00A8B5', flex: Math.max(1, data.web?.share || 0), color: 'white' }}>limpack.uy · {data.web?.share || 0}%</div>
            <div style={{ background: '#FFE600', flex: Math.max(1, data.ml?.share || 0), color: '#5C4500' }}>ML · {data.ml?.share || 0}%</div>
            <div style={{ background: '#1A3461', flex: Math.max(1, data.b2b?.share || 0), color: 'white' }}>B2B · {data.b2b?.share || 0}%</div>
          </div>
          <div className="cr-note">
            <strong>Lectura estratégica:</strong> ML es minoría del volumen pero cumple rol de <em>ancla de precio</em>. Mientras siga &gt;25% más caro que limpack.uy, refuerza el claim "promedio del mercado" de la web.
          </div>
        </div>
      </div>
      <Estilos />
    </AdminLayout>
  )
}

function CanalCard({ color, icon, nombre, tipo, data, href, loading, fmt, extra }) {
  return (
    <Link to={href} className="cr-canal-card" style={{ borderTopColor: color }}>
      <div className="cr-canal-head">
        <div>
          <div className="cr-canal-name">{nombre}</div>
          <div className="cr-canal-meta">{tipo}</div>
        </div>
        <div className="cr-canal-icon">{icon}</div>
      </div>
      <div className="cr-canal-stats">
        <div className="cr-stat-box">
          <div className="cr-stat-lbl">Ventas mes</div>
          <div className="cr-stat-val">{loading ? '…' : fmt(data?.ventas_mes)}</div>
          {data?.share != null && <div className="cr-stat-sub">{data.share}% del total</div>}
        </div>
        <div className="cr-stat-box">
          <div className="cr-stat-lbl">Pedidos</div>
          <div className="cr-stat-val">{loading ? '…' : data?.pedidos}</div>
        </div>
        <div className="cr-stat-box">
          <div className="cr-stat-lbl">Ticket promedio</div>
          <div className="cr-stat-val">{loading ? '…' : fmt(data?.ticket)}</div>
        </div>
        <div className="cr-stat-box">
          <div className="cr-stat-lbl">Margen neto</div>
          <div className="cr-stat-val">{loading ? '…' : `${data?.margen_pct || 0}%`}</div>
        </div>
      </div>
      {extra}
      <div className="cr-canal-link">Ver canal →</div>
    </Link>
  )
}

function Estilos() {
  return (
    <style>{`
      .cr-wrap { max-width: 1400px; margin: 0 auto; padding: 0 4px 60px; }
      .cr-head { background: #fff; border: 1px solid #E6E2D8; border-radius: 10px; padding: 14px 18px; margin-bottom: 12px; }
      .cr-eyebrow { font-size: 10.5px; letter-spacing: 0.8px; color: #888780; text-transform: uppercase; font-weight: 600; }
      .cr-title { margin: 4px 0 4px; font-size: 22px; font-weight: 600; color: #1A3461; }
      .cr-sub { font-size: 12.5px; color: #5F5E5A; }

      .cr-kpis { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-bottom: 12px; }
      .cr-kpi { background: #fff; border: 1px solid #E6E2D8; border-radius: 10px; padding: 13px 16px; }
      .cr-kpi-lbl { font-size: 10px; letter-spacing: 0.8px; color: #888780; text-transform: uppercase; font-weight: 600; margin-bottom: 6px; }
      .cr-kpi-num { font-size: 24px; font-weight: 700; color: #1A3461; line-height: 1; }
      .cr-kpi-delta { font-size: 11px; color: #1D9E75; margin-top: 5px; font-weight: 500; }
      .cr-kpi-delta.mute { color: #888780; font-weight: 400; }

      .cr-canales { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin-bottom: 12px; }
      @media (max-width: 980px) { .cr-canales { grid-template-columns: 1fr; } }
      .cr-canal-card {
        background: #fff; border: 1px solid #E6E2D8; border-top: 4px solid #ccc; border-radius: 10px;
        padding: 16px 18px; cursor: pointer; transition: border-color .2s; text-decoration: none; color: inherit;
        display: flex; flex-direction: column;
      }
      .cr-canal-card:hover { border-color: #00A8B5; }
      .cr-canal-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 14px; }
      .cr-canal-name { font-size: 16px; font-weight: 700; color: #1A3461; }
      .cr-canal-meta { font-size: 11px; color: #888780; margin-top: 2px; }
      .cr-canal-icon { font-size: 22px; }
      .cr-canal-stats { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 14px; }
      .cr-stat-box { background: #F8F6F1; border-radius: 6px; padding: 8px 10px; }
      .cr-stat-lbl { font-size: 9.5px; letter-spacing: 0.5px; color: #888780; text-transform: uppercase; font-weight: 600; }
      .cr-stat-val { font-size: 17px; font-weight: 700; color: #1A3461; line-height: 1.2; margin-top: 2px; }
      .cr-stat-sub { font-size: 10.5px; color: #5F5E5A; }
      .cr-card-extra { display: flex; gap: 14px; margin: 8px 0 12px; padding: 8px 10px; background: #F8F6F1; border-radius: 6px; font-size: 11.5px; color: #1A3461; }
      .cr-card-extra > div { display: flex; flex-direction: column; }
      .cr-card-extra span { font-size: 10px; color: #888780; text-transform: uppercase; letter-spacing: 0.5px; font-weight: 600; }
      .cr-card-extra strong { font-size: 16px; font-weight: 700; }
      .cr-canal-link { font-size: 12px; color: #00A8B5; font-weight: 600; margin-top: auto; }

      .cr-card { background: #fff; border: 1px solid #E6E2D8; border-radius: 10px; padding: 16px 18px; margin-bottom: 12px; }
      .cr-card h3 { margin: 0 0 12px; font-size: 14px; font-weight: 600; color: #1A3461; }
      .cr-alert { padding: 8px 10px; border-radius: 6px; font-size: 12.5px; }
      .cr-alert.amber { background: #FAEEDA; color: #633806; border-left: 3px solid #BA7517; }
      .cr-alert.red { background: #FDEEEE; color: #791F1F; border-left: 3px solid #C53030; }

      .cr-bar { display: flex; gap: 4px; height: 36px; border-radius: 6px; overflow: hidden; margin-bottom: 12px; }
      .cr-bar > div { display: flex; align-items: center; padding-left: 12px; font-size: 12px; font-weight: 600; min-width: 60px; }

      .cr-note { background: #FFFBEF; border: 1px solid #E6D9B8; border-radius: 8px; padding: 11px 14px; font-size: 12px; color: #1A3461; line-height: 1.5; }
      .cr-note strong { color: #C5A059; }
    `}</style>
  )
}
